/**
 * The wire shapes for 02a Machine 2 — settlement, custody and remittance.
 *
 * Every field here exists because a **named guard reads it**, and the comment says which.
 * A request field nobody's predicate consults is a field that will drift out of meaning.
 *
 * ## Why this is not `SUPPORTED_TRIGGERS`
 *
 * `shipments.ts`'s list is Machine **1**'s, consumed by `ShipmentsService.apply`, which
 * then calls `applyTransition(SHIPMENT_MACHINE, …)`. Adding a `cod.*` trigger there would
 * make the endpoint accept it, pass validation, and then fail inside the wrong machine with
 * "no table row" — a 409 whose message describes a table the caller never asked about.
 * Machine 2 gets its own closed list and its own endpoints.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
/**
 * Machine 2's triggers, all of them since slice C.
 *
 * **Read by `CustodyService.decide`**, which refuses anything outside it before touching the
 * machine. It was exported and consumed by nothing for a whole slice — a closed list that
 * enforced no closure, which is the shape `docs/decisions/README.md` tracks.
 */
export const COD_SUPPORTED_TRIGGERS = [
    'cod.settle',
    'cod.confirm_custody',
    'cod.remit',
    'cod.discrepancy_open',
    'cod.discrepancy_resolve',
    'cod.freeze_custody',
    'cod.release_custody',
    'cod.dispose_per_ruling',
];
// `FEE_CODES` deliberately lives in `@tard/kernel` (`ledger.ts`) and not here: it is the
// `code` dimension of an account family, so the chart of accounts owns it. No schema in
// this file needs it, and a second copy would be a closed vocabulary stated twice.
/**
 * A counted amount. A **string** of integer minor units, never a number: an LBP shift total
 * passes 2^53 and JSON numbers are doubles (I2).
 */
export const countSchema = z.object({
    currency: z.enum(['USD', 'LBP', 'SYP']),
    amountMinor: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
});
// Why the non-rendered strings below are exempt from the write-time phone check (ADR-0072).
const AMOUNT_REASON = 'minor units, digits only by regex; read as a BigInt, and no view shows the text sent';
const ORDER_IDS_REASON = 'selects Orders by id; a response lists only the ids read back from the database';
/**
 * A Courier hands their shift's cash to a Hub.
 *
 * `counted` is what was physically counted, and `courierFloatReconciled` compares it
 * against the ledger exactly, per currency. `hubId` names the account
 * `platform.cash_at_hub.{hub}.{currency}` the money lands in, and `bankingEvidenced`'s
 * twin `hubKnown` checks it resolves.
 */
export const settleCodSchema = z.object({
    hubId: z.string().trim().min(1).max(64),
    counted: z.array(countSchema).min(1).max(3),
    /**
     * Absent means every outstanding `CodCollected` Order for this Courier.
     *
     * Present, it is a deliberate subset — which is the only way to settle a shift where one
     * Order's cash is short, until slice C encodes `DiscrepancyOpen`. Settle what is there,
     * leave the rest outstanding.
     */
    orderIds: z.array(z.string().trim().min(1).max(64)).min(1).max(500).optional(),
});
export const settleCodRendered = renderedText(settleCodSchema, {
    exempt: {
        hubId: 'must name an active Hub (hubKnown, lockHubWithin) before it is echoed; Hub ids are public ids',
        'counted.*.amountMinor': AMOUNT_REASON,
        'orderIds.*': ORDER_IDS_REASON,
    },
});
/** Cash banked out of a Hub's drawer. `bankingEvidenced` reads `bankingRef`. */
export const confirmCustodySchema = z.object({
    bankingRef: z.string().trim().min(1).max(200),
    orderIds: z.array(z.string().trim().min(1).max(64)).min(1).max(500).optional(),
});
export const confirmCustodyRendered = renderedText(confirmCustodySchema, {
    exempt: {
        bankingRef: 'stored only in the cod.custody_confirmed event payload; no response or view renders it',
        'orderIds.*': ORDER_IDS_REASON,
    },
});
/**
 * A Remittance run for one Merchant.
 *
 * Deliberately has no amount: what is paid is what the ledger says was collected, net of
 * the fees pinned on each Order. A caller who could name a figure could name a wrong one.
 */
export const remitSchema = z.object({
    /** Absent means every `HeldByPlatform` Order for this Merchant. */
    orderIds: z.array(z.string().trim().min(1).max(64)).min(1).max(500).optional(),
    /**
     * The reference of the transfer the operator actually made — an OMT or Whish receipt, a
     * bank reference, whatever the rail produced.
     *
     * **Required whenever the configured rail does not itself move money**, which today is
     * every rail: `remittance_rail` is `manual_stub`, the run posts the ledger entries and
     * marks the custody rows remitted, and *nothing calls a bank*. Without this the books
     * assert a payment nobody made, and the Merchant's own statement then shows them as paid.
     *
     * One per **run**, not per Order: a run pays many Orders in one transfer, and `runRef`
     * already groups them.
     *
     * It is not validated beyond being non-empty, and deliberately so — the platform cannot
     * check a reference issued by a rail it does not talk to (§10.6), and a format guess would
     * refuse real receipts. What it buys is that "remitted" means somebody asserted a transfer
     * and said which one, in an append-only record, rather than the books asserting it alone.
     */
    transferReference: z.string().trim().min(1).max(200).optional(),
});
export const remitRendered = renderedText(remitSchema, {
    refs: ['transferReference'],
    exempt: { 'orderIds.*': ORDER_IDS_REASON },
});
/**
 * Every state a Shipment's cash can be in, as screens name them (`codStatus.<state>`). The machine
 * that owns them is the kernel's `COD_MACHINE`, which the web cannot import; an API test pins this
 * list to it, so a state added there without copy here fails a build rather than reaching a screen as
 * a raw identifier — as `HeldInSuspense` and `DiscrepancyOpen` reached the exception queue, in every
 * language, from T2-4 until the P2-7 walk (2026-10-02).
 */
export const COD_STATES = [
    'CodPending',
    'CodCollected',
    'CodVoidedRto',
    'CodVoidedCancelled',
    'SettledToHub',
    'DiscrepancyOpen',
    'ClosedUnrecovered',
    'HeldByPlatform',
    'Remitted',
    'HeldInSuspense',
    'DisposedPerRuling',
];
/**
 * Why a custody act holds an Order rather than acting on it (P2-7c, ADR-0083): Machine 2's guard,
 * named — no rail taking runs, a Merchant not active, not clear of screening or screened longer ago
 * than compliance's window allows (P2-8b), a dispute open, a
 * compliance act without the authority it needs or a freeze not yet cleared, or a refusal the act
 * cannot name more closely — and a pinned price naming a fee code nobody named. A remittance run, its
 * preview, a freeze, a release and a disposition return these and no others (the API types its
 * `whyHeld` against this list), and the screens say each in words: the web's coverage gate requires
 * copy for every one, so a reason added here without copy fails a test rather than reaching an
 * operator as an identifier.
 */
export const CUSTODY_HOLD_REASONS = [
    'remittance_rail_unavailable',
    'merchant_not_active',
    'merchant_screening_not_clear',
    'merchant_screening_not_current',
    'dispute_hold',
    'compliance_authority_required',
    'not_cleared',
    'transition_refused',
    'unknown_fee_code',
];
/**
 * Why custody was frozen. 02a names exactly two, and the enum is closed because
 * `complianceCleared` later reads the reason back off the stream to decide whether an I7
 * re-screen is required. A freeze whose reason nobody constrained is a freeze nobody can
 * lawfully release.
 */
export const FREEZE_REASONS = ['i7_screening_hit', 'merchant_offboarding'];
/**
 * Who bears a cash loss — Q20's liability question, as a runtime choice.
 *
 * 02a names all three side by side and all three accounts are seeded, so the ruling belongs
 * to the adjudicator at the moment they rule. **No default, deliberately**: a fallback of
 * `loss_writeoff` would be the platform quietly absorbing losses because nobody chose.
 */
export const DISCREPANCY_ALLOCATIONS = [
    'courier_receivable',
    'loss_writeoff',
    'merchant_bears',
];
/** 02a's three lawful dispositions. Which is *lawful when* is Q1's, and counsel's. */
export const DISPOSITION_INSTRUMENTS = ['surrender', 'escheat', 'court_order'];
const ref = (max = 200) => z.string().trim().min(1).max(max);
/**
 * A cash count did not match the ledger, and the difference goes to suspense.
 *
 * `evidenceRef` is read by `discrepancyEvidenced`, which also requires a non-zero difference
 * in some currency — opening a discrepancy over a balance that agrees would put a suspense
 * entry against nothing. `counted` is the recount; absent, the whole outstanding balance is
 * treated as missing, which is the `shipment.lost` case.
 *
 * `hubId` names the drawer the counted cash went into, and is required once any was counted
 * (P2-7b, ADR-0083): the counted cash is posted there, so a short count leaves nothing in the
 * Courier's hand. A loss counts nothing and names no Hub.
 */
export const openDiscrepancySchema = z.object({
    evidenceRef: ref(),
    reason: z.enum(['count_mismatch', 'cash_custody_loss']).default('count_mismatch'),
    counted: z.array(countSchema).max(3).optional(),
    hubId: z.string().trim().min(1).max(64).optional(),
    orderIds: z.array(z.string().trim().min(1).max(64)).min(1).max(500).optional(),
});
export const openDiscrepancyRendered = renderedText(openDiscrepancySchema, {
    exempt: {
        evidenceRef: 'stored only in the cod.discrepancy_opened event payload; no response or view renders it',
        'counted.*.amountMinor': AMOUNT_REASON,
        hubId: 'must name an active Hub (lockHubWithin) before it keys an account; Hub ids are public ids',
        'orderIds.*': ORDER_IDS_REASON,
    },
});
/**
 * An adjudicator rules on a discrepancy.
 *
 * `outcome` is the transition's **reason code** — it is what tells `→ SettledToHub` from
 * `→ ClosedUnrecovered`, what the envelope carries, and what stops the custody clock on the
 * unrecovered branch. One field, three jobs. `adjudicationRef` is read by
 * `adjudicatedFromEventLog`; `allocation` has no default (see above).
 */
export const resolveDiscrepancySchema = z.object({
    outcome: z.enum(['recovered', 'unrecovered']),
    adjudicationRef: ref(),
    allocation: z.enum(DISCREPANCY_ALLOCATIONS),
    /** Cash that physically reached a Hub. Only a `recovered` ruling may name any. */
    recovered: z.array(countSchema).max(3).optional(),
    hubId: z.string().trim().min(1).max(64).optional(),
});
export const resolveDiscrepancyRendered = renderedText(resolveDiscrepancySchema, {
    exempt: {
        adjudicationRef: 'stored only in the cod.discrepancy_resolved event payload; no response or view renders it',
        'recovered.*.amountMinor': AMOUNT_REASON,
        hubId: 'not echoed; stored in the event, and must name an active Hub (lockHubWithin) before it keys an account',
    },
});
/**
 * A small count shortfall charged to the Courier by Hub finance (P2-8e, ADR-0083 §17): the reference of
 * the count sheet the ruling rests on, and nothing else. Who bears it (the Courier), what is recovered
 * (nothing beyond what the count put in the drawer) and which drawer are not the caller's to choose.
 */
export const chargeShortfallSchema = z.object({ countSheetRef: ref() });
export const chargeShortfallRendered = renderedText(chargeShortfallSchema, {
    exempt: {
        countSheetRef: 'stored only in the cod.discrepancy_resolved event payload; no response or view renders it',
    },
});
/**
 * Why Hub finance may not charge a shortfall to the Courier (P2-8e), each said in words at the cash
 * desk. `withinShortfallThreshold` is true exactly when none of these applies.
 */
export const SHORTFALL_CHARGE_REFUSALS = [
    /** Not opened by a count, or opened before the count's whole shortfall was recorded with it. */
    'shortfall_not_from_count',
    /** No cash of this Order reached a drawer, so nothing could settle at a Hub. */
    'shortfall_cash_not_in_drawer',
    /** An over-count, which no ruling clears. */
    'shortfall_not_short',
    /** No threshold on file, or none for a currency the count touched. */
    'shortfall_threshold_unset',
    /** The count's whole shortfall is larger than the threshold in some currency. */
    'shortfall_over_threshold',
];
/** `complianceAuthority` reads `reason`; the facet itself comes from the principal. */
export const freezeCustodySchema = z.object({
    reason: z.enum(FREEZE_REASONS),
    orderIds: z.array(z.string().trim().min(1).max(64)).min(1).max(500).optional(),
});
export const freezeCustodyRendered = renderedText(freezeCustodySchema, {
    exempt: { 'orderIds.*': ORDER_IDS_REASON },
});
/** `complianceCleared` reads `clearanceRef`, and re-screens when the freeze was an I7 hit. */
export const releaseCustodySchema = z.object({
    clearanceRef: ref(),
    orderIds: z.array(z.string().trim().min(1).max(64)).min(1).max(500).optional(),
});
export const releaseCustodyRendered = renderedText(releaseCustodySchema, {
    exempt: {
        clearanceRef: 'stored only in the cod.custody_released event payload; no response or view renders it',
        'orderIds.*': ORDER_IDS_REASON,
    },
});
/**
 * Frozen funds disposed of per a counsel-directed instrument.
 *
 * `counselDirectedInstrument` reads both fields. **Q1 leaves the policy open** — who may
 * sign, and when each instrument is lawful — and this schema does not answer it. What it
 * fixes is the *shape* 02a specifies, so the day counsel rules the change is a runbook and
 * a signed instrument rather than a deploy.
 */
export const disposePerRulingSchema = z.object({
    instrumentRef: ref(),
    instrumentKind: z.enum(DISPOSITION_INSTRUMENTS),
    orderIds: z.array(z.string().trim().min(1).max(64)).min(1).max(500).optional(),
});
export const disposePerRulingRendered = renderedText(disposePerRulingSchema, {
    exempt: {
        instrumentRef: 'stored only in the cod.disposed_per_ruling event payload; no response or view renders it',
        'orderIds.*': ORDER_IDS_REASON,
    },
});
export const statementQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(500).default(200),
    status: z.enum(['all', 'unremitted', 'remitted']).default('all'),
});
/** `policy_config` keys Machine 2 reads. A missing key fails its guard closed (§10.6). */
export const COD_POLICY_KEYS = {
    remittanceRail: 'remittance_rail',
    courierShortfallThreshold: 'courier_shortfall_threshold',
};
//# sourceMappingURL=custody.js.map